import { Controller } from "@hotwired/stimulus";

/**
 * News feed — photos, videos and documents of a post, sent ONE file per request.
 * Each file shows its own progress; a file that fails can be sent again
 * alone, with the same client id, so a retry never makes a duplicate.
 */
export default class extends Controller {
  static targets = ["list", "input", "item", "usage"];
  static values = { url: String, uploading: String, failed: String };

  pick() {
    Array.from(this.inputTarget.files).forEach((file) => this.send(file, this.uuid()));
    this.inputTarget.value = "";
  }

  // `crypto.randomUUID` only exists on a secure origin (https, localhost):
  // over plain http the id is drawn from `getRandomValues`, which is always there.
  uuid() {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  send(file, uuid, row = null) {
    row = row || this.placeholder(file);
    row.classList.remove("is-failed");
    row.querySelector("[data-role=state]").textContent = this.uploadingValue;
    const bar = row.querySelector(".ds-newsfile__bar > span");
    const body = new FormData();
    body.append("file", file);
    body.append("client_uuid", uuid);

    const request = new XMLHttpRequest();
    request.open("POST", this.urlValue);
    request.setRequestHeader("Accept", "application/json");
    request.setRequestHeader("X-CSRF-Token", document.querySelector('meta[name="csrf-token"]')?.content || "");
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) bar.style.width = `${Math.round((event.loaded / event.total) * 100)}%`;
    };
    request.onload = () => {
      if (request.status === 201) {
        const answer = JSON.parse(request.responseText);
        row.outerHTML = answer.html;
        this.showUsage(answer.usage);
        window.g4sRenderIcons?.();
      } else {
        let message = this.failedValue;
        try { message = JSON.parse(request.responseText).error || message; } catch (_) { /* keep the default */ }
        this.fail(row, message, file, uuid, request.status !== 422);
      }
    };
    request.onerror = () => this.fail(row, this.failedValue, file, uuid, true);
    request.send(body);
  }

  fail(row, message, file, uuid, retryable) {
    row.classList.add("is-failed");
    const state = row.querySelector("[data-role=state]");
    state.textContent = message;
    if (!retryable) return;
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "ds-newsfile__retry";
    retry.textContent = "↻";
    retry.setAttribute("aria-label", message);
    retry.addEventListener("click", () => { retry.remove(); this.send(file, uuid, row); });
    row.appendChild(retry);
  }

  placeholder(file) {
    const row = document.createElement("li");
    row.className = "ds-newsfile";
    const name = document.createElement("span");
    name.className = "ds-newsfile__name";
    name.textContent = file.name;
    const bar = document.createElement("span");
    bar.className = "ds-newsfile__bar";
    bar.appendChild(document.createElement("span"));
    const state = document.createElement("span");
    state.className = "ds-text-meta";
    state.dataset.role = "state";
    row.append(name, bar, state);
    this.listTarget.appendChild(row);
    return row;
  }

  async remove(event) {
    const button = event.currentTarget;
    const response = await fetch(button.dataset.url, {
      method: "DELETE",
      headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      credentials: "same-origin",
    });
    if (!response.ok) return;
    button.closest(".ds-newsfile").remove();
    const answer = await response.json().catch(() => ({}));
    this.showUsage(answer.usage);
  }

  // What the files weigh against the limit of the post.
  showUsage(text) {
    if (text && this.hasUsageTarget) this.usageTarget.textContent = text;
  }
}
